<script>
    import Select from "svelte-select/Select.svelte";

    import {
        isObjectEmpty,
        showError,
        showException,
    } from "../../../Utils/utilFunctions.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import { NotificationModal } from "../../../Components/Common/index.svelte";

    export let providerDetails = [];
    export let vendorDetails = {};
    export let isModalOpen = false;
    export let reload = () => {};
    export let closeModal = () => {};
    export let isNewVendor = true;

    let backupVendorDetails = {};

    let selectedHospital = {},
        isLoadingHospitals = false,
        isUpdatingVendor = false;

    $: if (isModalOpen && !isNewVendor) _syncHospitalDetails();

    $: isVendorExists = _checkVendorExisting(selectedHospital, vendorDetails);

    $: isSubmitBtnDisabled =
        !vendorDetails.providerNumber ||
        isObjectEmpty(selectedHospital) ||
        isVendorExists;

    const submit = async () => {
        _syncVendorHospital();

        const providerDetails = _syncProviderDetails();

        isUpdatingVendor = true;

        await updateVendor(providerDetails);

        isUpdatingVendor = false;

        reload();
    };

    const updateVendor = async (obj) => {
        await apiMethods.putMethod
            .updateAccountProvider(obj)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        `Failed to ${
                            isNewVendor ? "create" : "edit"
                        } vendor details, please refresh the page and try again and contact support if the issue persists.`,
                    );
            })
            .catch((err) => showException(err));
    };

    const closeEditModal = () => {
        clearSelectedHospital();
        closeModal();
    };

    const loadHospitals = async (searchText) => {
        if (!searchText) return [];

        isLoadingHospitals = true;

        let res = await apiMethods.getMethod.getHospitals(0, 999999, {
            search: searchText,
        });

        isLoadingHospitals = false;

        // return results without restrictions - this action ONLY managed by ADMIN
        return res?.results ?? [];
    };

    const clearSelectedHospital = () => (selectedHospital = {});

    // HELPER FUNCTIONS
    const _syncVendorHospital = () => {
        vendorDetails.hospitalName = selectedHospital.details.name;
        vendorDetails.hospitalId = selectedHospital.details.id;
    };

    const _syncProviderDetails = () => {
        const selectedProvider = providerDetails.find(
            (provider) => provider.number == vendorDetails.providerNumber,
        );

        const providerHasSelectedHospital = selectedProvider.hospitals.some(
            (hospital) => hospital.hospitalId == vendorDetails.hospitalId,
        );

        if (!isObjectEmpty(backupVendorDetails)) {
            if (
                backupVendorDetails.hospitalId != selectedHospital.details?.id
            ) {
                selectedProvider.hospitals = selectedProvider.hospitals.filter(
                    (hospital) =>
                        hospital.hospitalId != backupVendorDetails.hospitalId,
                );
                backupVendorDetails = {};
            }
        }

        if (!providerHasSelectedHospital)
            return {
                ...selectedProvider,
                hospitals: [
                    ...selectedProvider.hospitals,
                    {
                        hospitalId: vendorDetails.hospitalId,
                        pipVendorNumber: vendorDetails.pipVendorNumber,
                    },
                ],
            };
        else
            return {
                ...selectedProvider,
                hospitals: [
                    ...selectedProvider.hospitals.map((hospital) => {
                        if (hospital.hospitalId != vendorDetails.hospitalId)
                            return hospital;
                        else
                            return {
                                ...hospital,
                                pipVendorNumber: vendorDetails.pipVendorNumber,
                            };
                    }),
                ],
            };
    };

    const _syncHospitalDetails = async () => {
        selectedHospital.details = await apiMethods.getMethod.getHospitalById(
            vendorDetails.hospitalId,
        );

        backupVendorDetails = { ...vendorDetails };
    };

    const _checkVendorExisting = (hospital = {}, vendor = {}) => {
        if (isObjectEmpty(hospital) || isObjectEmpty(vendor)) return false;

        if (!vendor.providerNumber || !vendor.pipVendorNumber) return false;

        const aimingHospitalId = hospital.id;
        const { providerNumber, pipVendorNumber: vendorPip } = vendor;

        const aimingProviderHospitals = providerDetails.find(
            (provider) => provider.number == providerNumber,
        ).hospitals;

        if (!aimingProviderHospitals || !aimingProviderHospitals.length)
            return false;

        return aimingProviderHospitals.some(
            ({ hospitalId, pipVendorNumber }) =>
                hospitalId == aimingHospitalId && vendorPip == pipVendorNumber,
        );
    };

    $: enableCreateEdit = vendorDetails.providerNumber && selectedHospital.details && vendorDetails.pipVendorNumber;
</script>

<NotificationModal
    title={`${isNewVendor ? "Create" : "Edit"} Public In Private Vendor Number`}
    modalMaxWidth="max-w-3xl"
    {isModalOpen}
    cancelText="Cancel"
    closeModal={closeEditModal}
    closeModalFunction={closeEditModal}
    isConfirmDisabled={!enableCreateEdit}
    submitText={`${isNewVendor ? (isUpdatingVendor ? "Creating" : "Create") : isUpdatingVendor ? "Updating" : "Update"}`}
    confirmAction={submit}
>
    <div class="flex flex-1 flex-col gap-y-4">
        <div class="flex gap-x-4">
            <div class="grid grid-cols-3 gap-x-2 gap-y-4 px-2 w-full">
                <p class="flex col-span-1">Provider Number *:</p>

                <select
                    class="select select-sm select-bordered flex col-span-2"
                    bind:value={vendorDetails.providerNumber}
                >
                    {#each providerDetails as provider}
                        <option
                            value={provider.number}
                            selected={vendorDetails.providerNumber ==
                                provider.number}
                        >
                            {provider.number}
                        </option>
                    {/each}
                </select>
            </div>
        </div>

        <div class="flex gap-x-4">
            <div
                class="flex items-center grid grid-cols-3 gap-x-2 gap-y-4 px-2 w-full"
            >
                <p class="flex col-span-1">Hospital *:</p>

                <div class="flex items-center col-span-2">
                    <Select
                        bind:value={selectedHospital.details}
                        loadOptions={loadHospitals}
                        itemId="id"
                        label="name"
                        on:clear={clearSelectedHospital}
                        noOptionsMessage="No hospitals found"
                        placeholder={isLoadingHospitals ? "Loading..." : ""}
                         --placeholder-color="#9CA3AF"
                        --height="32px"
                        --value-container-padding="0"
                        --padding="0 0 0 12px"
                        --font-size="14px"
                    />
                </div>
            </div>
        </div>

        <div class="flex gap-x-4">
            <div class="grid grid-cols-3 gap-x-2 gap-y-4 px-2 w-full">
                <p class="flex col-span-1">PIP Vendor Number *:</p>

                <input
                    type="text"
                    class="input input-sm input-bordered flex col-span-2"
                    bind:value={vendorDetails.pipVendorNumber}
                />
            </div>
        </div>

        {#if isVendorExists}
            <p class="text-error">
                Vendor already exists, please check before submit.
            </p>
        {/if}
    </div>
</NotificationModal>
